import { useEffect, useRef } from 'react';

/**
 * The behaviour every dialog in the app is expected to have but none of them
 * had: Escape closes it, the page behind it stops scrolling, and focus moves
 * into the dialog so keyboard and screen-reader users aren't left tabbing
 * through the page underneath.
 *
 * Returns a ref to put on the dialog's outermost element.
 */
export const useModalBehavior = (onClose: () => void) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Read through a ref so a caller passing an inline arrow function doesn't
  // rebind the key listener on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    // Restore the exact previous value rather than clearing it, so nested
    // dialogs don't hand scrolling back to the page too early.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Return focus where it was when the dialog closes — usually the button
    // that opened it.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const firstFocusable = containerRef.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, select, button, [href], [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  return containerRef;
};

export default useModalBehavior;
